<template>
  <div class="route-editor-page">
    <div class="editor-header">
      <div class="header-left">
        <button @click="goBack" class="back-button">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M19 12H5M12 19l-7-7 7-7"/>
          </svg>
          Back to History
        </button>
        <h1>Route Editor: {{ route?.name || 'Loading...' }}</h1>
      </div>
      <div class="header-right">
        <button 
          @click="saveRoute" 
          class="save-button"
          :disabled="!hasChanges || isSaving"
          :class="{ loading: isSaving }"
        >
          <span v-if="isSaving" class="loading-spinner"></span>
          {{ isSaving ? 'Saving...' : 'Save as New Route' }}
        </button>
      </div>
    </div>

    <div class="editor-content" v-if="route">
      <!-- Left Panel -->
      <div class="left-panel">
        <div class="route-overview">
          <h3>Route Overview</h3>
          <div class="overview-stats">
            <div class="stat-item">
              <span class="stat-label">Total Distance:</span>
              <span class="stat-value">{{ formatDistance(totalDistance) }}</span>
            </div>
            <div class="stat-item">
              <span class="stat-label">Total Locations:</span>
              <span class="stat-value">{{ totalLocations }}</span>
            </div>
            <div class="stat-item">
              <span class="stat-label">Active Vehicles:</span>
              <span class="stat-value">{{ activeVehicles }}</span>
            </div>
          </div>
        </div>

        <!-- Vehicle Routes List -->
        <div class="vehicle-routes">
          <h3>Vehicle Routes</h3>
          <div class="routes-list">
            <div 
              v-for="(vehicleRoute, index) in editableRoutes" 
              :key="index"
              class="route-item"
              :class="{ active: selectedVehicleIndex === index }"
              @click="selectVehicle(index)"
            >
              <div class="route-header">
                <div class="vehicle-info">
                  <span class="vehicle-color" :style="{ backgroundColor: vehicleColors[index] }"></span>
                  <span class="vehicle-name">{{ getVehicleDisplayName(vehicleRoute, index) }}</span>
                </div>
                <div class="route-stats">
                  <span class="distance">{{ formatDistance(vehicleRoute.distance || 0) }}</span>
                  <span class="stops">{{ vehicleRoute.stops?.length || 0 }} stops</span>
                </div>
              </div>
              
              <!-- Location List for Selected Vehicle -->
              <div v-if="selectedVehicleIndex === index" class="location-list">
                <div 
                  v-for="(stopIndex, stopIdx) in vehicleRoute.stops" 
                  :key="`${index}-${stopIndex}-${stopIdx}`"
                  class="location-item"
                  :class="{ 
                    'is-depot': isDepotLocation(stopIndex),
                    'selected': selectedLocationIndices.includes(stopIndex),
                    'multi-selected': isMultiSelectMode && selectedLocationIndices.includes(stopIndex) && selectedLocationIndices.length > 1
                  }"
                  @click.stop="selectLocation(stopIndex, $event.ctrlKey || $event.metaKey)"
                >
                  <div class="location-info">
                    <span class="location-name">{{ getLocationName(stopIndex) }}</span>
                    <span v-if="isDepotLocation(stopIndex)" class="depot-badge">DEPOT</span>
                    <span v-else class="location-index">#{{ stopIndex }}</span>
                  </div>
                  <div class="location-details">
                    <span v-if="!isDepotLocation(stopIndex)" class="demand">
                      {{ getLocationDemand(stopIndex) }} units
                    </span>
                    <span class="coordinates">
                      {{ formatCoordinates(getLocationCoords(stopIndex)) }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Map Container -->
      <div class="map-container">
        <div ref="mapContainer" class="map-display"></div>
        <div ref="loadingIndicator" class="loading-indicator">
          Loading map...
        </div>
      </div>

      <!-- Right Panel -->
      <div class="right-panel">
        <!-- Vehicle Assignment Panel -->
        <div class="vehicle-assignment" v-if="selectedLocationIndices.length > 0">
          <h3>Location Assignment</h3>
          <div class="assignment-info">
            <div class="current-location">
              <h4 v-if="selectedLocationIndices.length === 1">
                {{ getLocationName(selectedLocationIndices[0]) }}
              </h4>
              <h4 v-else>
                {{ selectedLocationIndices.length }} Locations Selected
              </h4>
              
              <!-- Show selected locations list for multi-select -->
              <div v-if="isMultiSelectMode && selectedLocationIndices.length > 1" class="selected-locations-list">
                <div class="selected-location-item" v-for="locationIndex in selectedLocationIndices" :key="locationIndex">
                  <span class="location-name">{{ getLocationName(locationIndex) }}</span>
                  <span class="location-assignment">
                    ({{ getLocationAssignedVehicles(locationIndex).length > 0 ? 
                        getVehicleDisplayName(editableRoutes[getLocationAssignedVehicles(locationIndex)[0]], getLocationAssignedVehicles(locationIndex)[0]) : 
                        'Unassigned' }})
                  </span>
                </div>
              </div>
              
              <p>Currently assigned to: <strong>{{ getCurrentAssignedVehicle() }}</strong></p>
            </div>
            
            <div class="assignment-controls">
              <label>
                {{ selectedLocationIndices.length === 1 ? 'Assign to Vehicle:' : 'Assign all selected to Vehicle:' }}
              </label>
              <select 
                v-model="newVehicleAssignment" 
                @change="assignLocationToVehicle"
                class="vehicle-select"
              >
                <option value="">Remove from route</option>
                <option 
                  v-for="(vehicleRoute, index) in editableRoutes" 
                  :key="index" 
                  :value="index"
                >
                  {{ getVehicleDisplayName(vehicleRoute, index) }}
                </option>
              </select>
              
              <button 
                v-if="selectedLocationIndices.length > 1" 
                @click="clearSelection"
                class="clear-selection-btn"
              >
                Clear Selection ({{ selectedLocationIndices.length }})
              </button>
            </div>
          </div>
        </div>

        <!-- Instructions Panel -->
        <div class="instructions-panel" v-else>
          <h3>Instructions</h3>
          <div class="instructions-content">
            <div class="instruction-item">
              <div class="instruction-icon">🗺️</div>
              <div class="instruction-text">
                <strong>Select Location:</strong> Click on any marker to select a location. Hold Ctrl (or Cmd on Mac) and click to select multiple locations.
              </div>
            </div>
            <div class="instruction-item">
              <div class="instruction-icon">🚛</div>
              <div class="instruction-text">
                <strong>Vehicle Routes:</strong> Click on vehicle routes in the left panel to expand and view stops
              </div>
            </div>
            <div class="instruction-item">
              <div class="instruction-icon">🔄</div>
              <div class="instruction-text">
                <strong>Reassign:</strong> Select a location, then use the dropdown to assign it to a different vehicle
              </div>
            </div>
            <div class="instruction-item">
              <div class="instruction-icon">💾</div>
              <div class="instruction-text">
                <strong>Save Changes:</strong> Click "Save as New Route" when you're done editing
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div v-else class="loading-state">
      <div class="loading-spinner large"></div>
      <p>Loading route data...</p>
    </div>
  </div>
</template>

<script>
import mapboxgl from 'mapbox-gl'
import 'mapbox-gl/dist/mapbox-gl.css'
import axios from 'axios'
import VehiclesService from '@/services/VehiclesService'

const API_URL = process.env.VUE_APP_API_URL || 'http://localhost:5000'

export default {
  name: 'RouteEditor',
  data() {
    return {
      route: null,
      vehicles: [],
      map: null,
      accessToken: process.env.VUE_APP_MAPBOX_ACCESS_TOKEN,
      
      // Editable state
      editableRoutes: [],
      originalRoutes: [],
      selectedVehicleIndex: 0,
      selectedLocationIndex: null,
      newVehicleAssignment: '',
      
      // Map state
      locationMarkers: [],
      routeLines: [],
      vehicleColors: [],
      
      // UI state
      isSaving: false,
      hasChanges: false,
      
      // Multi-select state
      selectedLocationIndices: [], // Array of selected location indices
      isMultiSelectMode: false
    }
  },
  computed: {
    totalDistance() {
      return this.editableRoutes.reduce((total, route) => total + (route.distance || 0), 0)
    },
    totalLocations() {
      const uniqueLocations = new Set()
      this.editableRoutes.forEach(route => {
        if (route.stops) {
          route.stops.forEach(stop => uniqueLocations.add(stop))
        }
      })
      return uniqueLocations.size
    },
    activeVehicles() {
      return this.editableRoutes.filter(route => route.stops && route.stops.length > 0).length
    }
  },
  async mounted() {
    await this.loadRouteData()
    await this.loadVehicles()
    this.initializeEditableState()
    
    // Ensure we have route data before initializing map
    if (this.route && this.route.allLocations) {
      this.initializeMap()
    } else {
      console.error('Route data not loaded properly')
    }
  },
  beforeUnmount() {
    if (this.map) {
      this.map.remove()
    }
  },
  methods: {
    async loadRouteData() {
      try {
        const routeId = this.$route.params.id
        const response = await axios.get(`${API_URL}/api/analytics/saved-routes/${routeId}`)
        this.route = response.data
      } catch (error) {
        console.error('Error loading route data:', error)
        alert('Failed to load route data')
        this.goBack()
      }
    },

    async loadVehicles() {
      try {
        const response = await VehiclesService.getVehicles()
        this.vehicles = response.data || []
      } catch (error) {
        console.error('Error loading vehicles:', error)
      }
    },

    initializeEditableState() {
      if (this.route && this.route.routes) {
        this.editableRoutes = JSON.parse(JSON.stringify(this.route.routes))
        this.originalRoutes = JSON.parse(JSON.stringify(this.route.routes))
        this.generateVehicleColors()
      }
    },

    generateVehicleColors() {
      const colors = [
        '#4285F4', '#EA4335', '#FBBC05', '#34A853', '#FF6D01',
        '#46BDC6', '#7C4DFF', '#1B9E77', '#D95F02', '#7570B3'
      ]
      if (this.editableRoutes && this.editableRoutes.length > 0) {
        this.vehicleColors = Array(this.editableRoutes.length).fill().map((_, i) => colors[i % colors.length])
      } else {
        this.vehicleColors = []
      }
    },

    initializeMap() {
      if (!this.accessToken) {
        console.error('Mapbox access token not found')
        return
      }

      mapboxgl.accessToken = this.accessToken

      this.map = new mapboxgl.Map({
        container: this.$refs.mapContainer,
        style: 'mapbox://styles/mapbox/streets-v11',
        center: [28.9784, 41.0082],
        zoom: 10
      })

      this.map.addControl(new mapboxgl.NavigationControl(), 'bottom-right')
      this.map.addControl(new mapboxgl.ScaleControl(), 'bottom-left')

      this.map.on('load', () => {
        this.hideLoadingIndicator()
        this.addLocationMarkers()
        this.drawRouteLines()
        this.fitMapToRoute()
      })
    },

    addLocationMarkers() {
      if (!this.route.allLocations) return

      this.clearMarkers()

      // Ensure we have colors generated
      if (!this.vehicleColors || this.vehicleColors.length === 0) {
        this.generateVehicleColors()
      }

      this.route.allLocations.forEach((coords, index) => {
        const isDepot = this.isDepotLocation(index)
        const locationName = this.getLocationName(index)
        const assignedVehicles = this.getLocationAssignedVehicles(index)
        
        // Create marker element
        const el = document.createElement('div')
        el.className = isDepot ? 'depot-marker' : 'location-marker'
        
        // Set marker color based on assignment
        if (!isDepot && assignedVehicles.length > 0) {
          if (assignedVehicles.length === 1) {
            const vehicleColor = this.vehicleColors[assignedVehicles[0]] || '#4285F4'
            el.style.backgroundColor = vehicleColor
          } else {
            // Multiple assignments - use gradient
            const colors = assignedVehicles.map(vehicleIndex => this.vehicleColors[vehicleIndex] || '#4285F4').filter(color => color)
            if (colors.length > 0) {
              el.style.background = `linear-gradient(45deg, ${colors.join(', ')})`
            }
          }
          el.classList.add('assigned-marker')
        }

        // Add click handler
        el.addEventListener('click', (e) => {
          e.stopPropagation()
          const isCtrlClick = e.ctrlKey || e.metaKey // Support both Ctrl (Windows) and Cmd (Mac)
          this.selectLocation(index, isCtrlClick)
        })

        // Create popup
        const popupContent = `
          <div class="location-popup">
            <h4>${locationName}</h4>
            <p><strong>Type:</strong> ${isDepot ? 'Depot' : 'Demand Point'}</p>
            ${assignedVehicles.length > 0 ? `<p><strong>Assigned to:</strong> ${assignedVehicles.map(v => this.getVehicleDisplayName(this.editableRoutes[v], v)).join(', ')}</p>` : ''}
            ${!isDepot ? `<p><strong>Demand:</strong> ${this.getLocationDemand(index)} units</p>` : ''}
            <p><strong>Coordinates:</strong> ${coords[1].toFixed(6)}, ${coords[0].toFixed(6)}</p>
          </div>
        `

        const marker = new mapboxgl.Marker({ element: el, anchor: 'center' })
          .setLngLat(coords)
          .setPopup(new mapboxgl.Popup({ offset: 25 }).setHTML(popupContent))
          .addTo(this.map)

        this.locationMarkers.push(marker)
      })
    },

    drawRouteLines() {
      this.clearRouteLines()

      // Ensure we have colors generated
      if (!this.vehicleColors || this.vehicleColors.length === 0) {
        this.generateVehicleColors()
      }

      this.editableRoutes.forEach((vehicleRoute, vehicleIndex) => {
        if (!vehicleRoute.stops || vehicleRoute.stops.length < 2) return

        const coordinates = []
        let totalDistance = 0

        // Build coordinate array and calculate distance
        for (let i = 0; i < vehicleRoute.stops.length; i++) {
          const stopIndex = vehicleRoute.stops[i]
          const coords = this.route.allLocations[stopIndex]
          if (coords) {
            coordinates.push(coords)
            
            // Calculate haversine distance to next point
            if (i < vehicleRoute.stops.length - 1) {
              const nextStopIndex = vehicleRoute.stops[i + 1]
              const nextCoords = this.route.allLocations[nextStopIndex]
              if (nextCoords) {
                totalDistance += this.calculateHaversineDistance(coords, nextCoords)
              }
            }
          }
        }

        // Update route distance
        vehicleRoute.distance = totalDistance

        // Get color for this vehicle
        const vehicleColor = this.vehicleColors[vehicleIndex] || '#4285F4'

        // Create line segments
        for (let i = 0; i < coordinates.length - 1; i++) {
          const start = coordinates[i]
          const end = coordinates[i + 1]
          
          this.addRouteLineToMap(start, end, vehicleColor, vehicleIndex)
        }
      })

      this.checkForChanges()
    },

    addRouteLineToMap(start, end, color, vehicleIndex) {
      // Ensure we have a valid color
      if (!color) {
        console.warn(`No color provided for vehicle ${vehicleIndex}, using default`)
        color = '#4285F4' // Default blue color
      }

      const sourceId = `route-${vehicleIndex}-${start.join(',')}-${end.join(',')}`
      const layerId = `route-layer-${vehicleIndex}-${start.join(',')}-${end.join(',')}`

      // Check if source already exists
      if (this.map.getSource(sourceId)) {
        console.warn(`Source ${sourceId} already exists, skipping`)
        return
      }

      try {
        // Add source
        this.map.addSource(sourceId, {
          type: 'geojson',
          data: {
            type: 'Feature',
            properties: {},
            geometry: {
              type: 'LineString',
              coordinates: [start, end]
            }
          }
        })

        // Add layer
        this.map.addLayer({
          id: layerId,
          type: 'line',
          source: sourceId,
          layout: {
            'line-join': 'round',
            'line-cap': 'round'
          },
          paint: {
            'line-color': color,
            'line-width': 4,
            'line-opacity': 0.8
          }
        })

        this.routeLines.push({ sourceId, layerId })
      } catch (error) {
        console.error(`Error adding route line to map:`, error)
      }
    },

    calculateHaversineDistance(coord1, coord2) {
      const R = 6371000 // Earth's radius in meters
      const lat1 = coord1[1] * Math.PI / 180
      const lat2 = coord2[1] * Math.PI / 180
      const deltaLat = (coord2[1] - coord1[1]) * Math.PI / 180
      const deltaLng = (coord2[0] - coord1[0]) * Math.PI / 180

      const a = Math.sin(deltaLat/2) * Math.sin(deltaLat/2) +
                Math.cos(lat1) * Math.cos(lat2) *
                Math.sin(deltaLng/2) * Math.sin(deltaLng/2)
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a))

      return R * c
    },

    selectVehicle(index) {
      this.selectedVehicleIndex = index
      this.selectedLocationIndex = null
      this.newVehicleAssignment = ''
    },

    selectLocation(locationIndex, isCtrlClick = false) {
      if (isCtrlClick) {
        // Multi-select mode
        this.isMultiSelectMode = true
        
        if (this.selectedLocationIndices.includes(locationIndex)) {
          // Remove from selection
          this.selectedLocationIndices = this.selectedLocationIndices.filter(idx => idx !== locationIndex)
        } else {
          // Add to selection
          this.selectedLocationIndices.push(locationIndex)
        }
        
        // Update single selection for backward compatibility
        this.selectedLocationIndex = this.selectedLocationIndices.length > 0 ? this.selectedLocationIndices[0] : null
      } else {
        // Single select mode
        this.isMultiSelectMode = false
        this.selectedLocationIndices = [locationIndex]
        this.selectedLocationIndex = locationIndex
      }
      
      // Update vehicle assignment dropdown
      if (this.selectedLocationIndices.length === 1) {
        this.newVehicleAssignment = this.getLocationAssignedVehicles(this.selectedLocationIndices[0])[0] || ''
      } else if (this.selectedLocationIndices.length > 1) {
        // For multi-select, check if all have the same assignment
        const assignments = this.selectedLocationIndices.map(idx => this.getLocationAssignedVehicles(idx)[0])
        const uniqueAssignments = [...new Set(assignments.filter(a => a !== undefined))]
        this.newVehicleAssignment = uniqueAssignments.length === 1 ? uniqueAssignments[0] : ''
      } else {
        this.newVehicleAssignment = ''
      }
      
      // Highlight selected locations on map
      this.highlightSelectedLocations()
    },

    highlightSelectedLocations() {
      // Remove previous highlights and add new ones
      this.locationMarkers.forEach((marker, index) => {
        const el = marker.getElement()
        el.classList.remove('selected', 'multi-selected')
        
        if (this.selectedLocationIndices.includes(index)) {
          if (this.isMultiSelectMode && this.selectedLocationIndices.length > 1) {
            el.classList.add('multi-selected')
          } else {
            el.classList.add('selected')
          }
        }
      })
    },

    assignLocationToVehicle() {
      if (this.selectedLocationIndices.length === 0) return

      const newVehicleIndex = this.newVehicleAssignment === '' ? null : parseInt(this.newVehicleAssignment)

      // Process each selected location
      this.selectedLocationIndices.forEach(locationIndex => {
        // Skip depot locations
        if (this.isDepotLocation(locationIndex)) return

        const oldVehicleIndex = this.getLocationAssignedVehicles(locationIndex)[0]

        // Remove from old vehicle
        if (oldVehicleIndex !== undefined) {
          const oldRoute = this.editableRoutes[oldVehicleIndex]
          if (oldRoute.stops) {
            const stopIndex = oldRoute.stops.indexOf(locationIndex)
            if (stopIndex > -1) {
              oldRoute.stops.splice(stopIndex, 1)
            }
          }
        }

        // Add to new vehicle
        if (newVehicleIndex !== null) {
          const newRoute = this.editableRoutes[newVehicleIndex]
          if (!newRoute.stops) {
            newRoute.stops = []
          }
          if (!newRoute.stops.includes(locationIndex)) {
            newRoute.stops.push(locationIndex)
          }
        }
      })

      // Redraw map
      this.addLocationMarkers()
      this.drawRouteLines()
      this.checkForChanges()
    },

    clearSelection() {
      this.selectedLocationIndices = []
      this.selectedLocationIndex = null
      this.isMultiSelectMode = false
      this.newVehicleAssignment = ''
      this.highlightSelectedLocations()
    },

    getLocationAssignedVehicles(locationIndex) {
      const assigned = []
      this.editableRoutes.forEach((route, index) => {
        if (route.stops && route.stops.includes(locationIndex)) {
          assigned.push(index)
        }
      })
      return assigned
    },

    getCurrentAssignedVehicle() {
      if (this.selectedLocationIndices.length === 0) return 'None'
      
      if (this.selectedLocationIndices.length === 1) {
        const assigned = this.getLocationAssignedVehicles(this.selectedLocationIndices[0])
        if (assigned.length === 0) return 'None'
        if (assigned.length === 1) return this.getVehicleDisplayName(this.editableRoutes[assigned[0]], assigned[0])
        return assigned.map(i => this.getVehicleDisplayName(this.editableRoutes[i], i)).join(', ')
      } else {
        // Multiple locations selected - show summary
        const assignments = new Map()
        this.selectedLocationIndices.forEach(locationIndex => {
          const assigned = this.getLocationAssignedVehicles(locationIndex)
          assigned.forEach(vehicleIndex => {
            const vehicleName = this.getVehicleDisplayName(this.editableRoutes[vehicleIndex], vehicleIndex)
            assignments.set(vehicleIndex, vehicleName)
          })
        })
        
        if (assignments.size === 0) return 'None'
        if (assignments.size === 1) return Array.from(assignments.values())[0]
        return `Multiple vehicles (${assignments.size})`
      }
    },

    checkForChanges() {
      this.hasChanges = JSON.stringify(this.editableRoutes) !== JSON.stringify(this.originalRoutes)
    },

    async saveRoute() {
      if (!this.hasChanges || this.isSaving) return

      this.isSaving = true

      try {
        // Create new route data
        const newRouteData = {
          ...this.route,
          name: `${this.route.name} (Edited)`,
          routes: this.editableRoutes,
          total_distance: this.totalDistance,
          timestamp: new Date().toISOString(),
          edited_from: this.route._id || this.route.id
        }

        // Remove ID so it creates a new record
        delete newRouteData._id
        delete newRouteData.id

        console.log('Saving route data:', newRouteData)

        const response = await axios.post(`${API_URL}/api/analytics/save-route`, newRouteData)

        console.log('Save response:', response.data)

        if (response.data && (response.data.success || response.data.message)) {
          alert('Route saved successfully!')
          this.$router.push('/history')
        } else {
          throw new Error('Failed to save route')
        }
      } catch (error) {
        console.error('Error saving route:', error)
        if (error.response) {
          console.error('Response data:', error.response.data)
          console.error('Response status:', error.response.status)
        }
        alert(`Failed to save route: ${error.response?.data?.error || error.message || 'Unknown error'}`)
      } finally {
        this.isSaving = false
      }
    },

    // Utility methods
    isDepotLocation(locationIndex) {
      if (this.route.multiDepot && this.route.depots) {
        return this.route.depots.includes(locationIndex)
      }
      return locationIndex === 0
    },

    getLocationName(locationIndex) {
      if (this.route.locationNames && this.route.locationNames[locationIndex]) {
        return this.route.locationNames[locationIndex]
      }
      if (this.isDepotLocation(locationIndex)) {
        return 'Depot'
      }
      return `Location ${locationIndex}`
    },

    getLocationCoords(locationIndex) {
      if (this.route.allLocations && this.route.allLocations[locationIndex]) {
        return this.route.allLocations[locationIndex]
      }
      return null
    },

    getLocationDemand(locationIndex) {
      if (this.isDepotLocation(locationIndex)) return 0
      if (this.route.demands && this.route.demands[locationIndex] !== undefined) {
        return this.route.demands[locationIndex]
      }
      return 1
    },

    getVehicleDisplayName(vehicleRoute, index) {
      if (vehicleRoute.vehicleId && this.vehicles.length > 0) {
        const vehicle = this.vehicles.find(v => v.id === vehicleRoute.vehicleId)
        return vehicle ? vehicle.plate : `Vehicle ${index + 1}`
      }
      return `Vehicle ${index + 1}`
    },

    formatDistance(distance) {
      return `${(distance / 1000).toFixed(1)}km`
    },

    formatCoordinates(coords) {
      if (!coords || coords.length !== 2) return 'N/A'
      return `${coords[1].toFixed(5)}, ${coords[0].toFixed(5)}`
    },

    fitMapToRoute() {
      if (!this.route.allLocations || this.route.allLocations.length === 0) return

      const bounds = new mapboxgl.LngLatBounds()
      this.route.allLocations.forEach(location => {
        bounds.extend(location)
      })

      this.map.fitBounds(bounds, { padding: 50 })
    },

    clearMarkers() {
      this.locationMarkers.forEach(marker => marker.remove())
      this.locationMarkers = []
    },

    clearRouteLines() {
      this.routeLines.forEach(({ sourceId, layerId }) => {
        if (this.map.getLayer(layerId)) {
          this.map.removeLayer(layerId)
        }
        if (this.map.getSource(sourceId)) {
          this.map.removeSource(sourceId)
        }
      })
      this.routeLines = []
    },

    hideLoadingIndicator() {
      if (this.$refs.loadingIndicator) {
        this.$refs.loadingIndicator.classList.remove('visible')
      }
    },

    goBack() {
      this.$router.push('/history')
    }
  }
}
</script>

<style scoped>
.route-editor-page {
  display: flex;
  flex-direction: column;
  height: 100vh;
  background: #f8f9fa;
}

.editor-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 2rem;
  background: white;
  border-bottom: 1px solid #dee2e6;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.header-left {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.back-button {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: #6c757d;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.9rem;
  transition: background-color 0.2s;
}

.back-button:hover {
  background: #5a6268;
}

.editor-header h1 {
  margin: 0;
  font-size: 1.5rem;
  color: #2c3e50;
  font-weight: 600;
}

.save-button {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: #28a745;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.2s;
}

.save-button:hover:not(:disabled) {
  background: #218838;
  box-shadow: 0 2px 8px rgba(40, 167, 69, 0.3);
}

.save-button:disabled {
  background: #6c757d;
  cursor: not-allowed;
}

.save-button.loading {
  background: #6c757d;
}

.editor-content {
  display: flex;
  flex: 1;
  overflow: hidden;
}

.left-panel {
  width: 350px;
  background: white;
  border-right: 1px solid #dee2e6;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
}

.right-panel {
  width: 320px;
  background: white;
  border-left: 1px solid #dee2e6;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
}

.route-overview {
  padding: 1.5rem;
  border-bottom: 1px solid #dee2e6;
}

.route-overview h3 {
  margin: 0 0 1rem 0;
  color: #2c3e50;
  font-size: 1.2rem;
}

.overview-stats {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.stat-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.stat-label {
  color: #6c757d;
  font-size: 0.9rem;
}

.stat-value {
  font-weight: 600;
  color: #2c3e50;
}

.vehicle-routes {
  flex: 1;
  padding: 1.5rem;
  border-bottom: 1px solid #dee2e6;
}

.vehicle-routes h3 {
  margin: 0 0 1rem 0;
  color: #2c3e50;
  font-size: 1.2rem;
}

.routes-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.route-item {
  border: 1px solid #dee2e6;
  border-radius: 8px;
  padding: 1rem;
  cursor: pointer;
  transition: all 0.2s;
  background: white;
}

.route-item:hover {
  border-color: #3498db;
  box-shadow: 0 2px 8px rgba(52, 152, 219, 0.1);
}

.route-item.active {
  border-color: #3498db;
  background: #e8f4fd;
}

.route-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.vehicle-info {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.vehicle-color {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 2px solid white;
  box-shadow: 0 1px 3px rgba(0,0,0,0.3);
}

.vehicle-name {
  font-weight: 600;
  color: #2c3e50;
}

.route-stats {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.25rem;
  font-size: 0.9rem;
}

.distance {
  font-weight: 600;
  color: #3498db;
}

.stops {
  color: #6c757d;
}

.location-list {
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #dee2e6;
}

.location-item {
  padding: 0.75rem;
  border-radius: 6px;
  margin-bottom: 0.5rem;
  cursor: pointer;
  transition: background-color 0.2s;
  border: 1px solid transparent;
}

.location-item:hover {
  background: #f8f9fa;
}

.location-item.selected {
  background: #fff3cd;
  border-color: #ffc107;
}

.location-item.is-depot {
  background: #fdf2e9;
  border-color: #f39c12;
}

.location-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
}

.location-name {
  font-weight: 600;
  color: #2c3e50;
}

.depot-badge {
  background: #f39c12;
  color: white;
  padding: 0.1rem 0.4rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
}

.location-index {
  background: #ecf0f1;
  color: #7f8c8d;
  padding: 0.1rem 0.4rem;
  border-radius: 12px;
  font-size: 0.75rem;
}

.location-details {
  display: flex;
  justify-content: space-between;
  font-size: 0.8rem;
  color: #6c757d;
}

.vehicle-assignment {
  padding: 1.5rem;
  background: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
}

.vehicle-assignment h3 {
  margin: 0 0 1rem 0;
  color: #2c3e50;
  font-size: 1.2rem;
}

.assignment-info {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.current-location h4 {
  margin: 0 0 0.5rem 0;
  color: #2c3e50;
}

.current-location p {
  margin: 0;
  color: #6c757d;
  font-size: 0.9rem;
}

.assignment-controls {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.assignment-controls label {
  font-weight: 600;
  color: #2c3e50;
  font-size: 0.9rem;
}

.vehicle-select {
  padding: 0.5rem;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  background: white;
  font-size: 0.9rem;
}

.instructions-panel {
  padding: 1.5rem;
  background: #f8f9fa;
}

.instructions-panel h3 {
  margin: 0 0 1rem 0;
  color: #2c3e50;
  font-size: 1.2rem;
}

.instructions-content {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.instruction-item {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 1rem;
  background: white;
  border-radius: 8px;
  border: 1px solid #dee2e6;
}

.instruction-icon {
  font-size: 1.5rem;
  flex-shrink: 0;
}

.instruction-text {
  font-size: 0.9rem;
  line-height: 1.4;
  color: #495057;
}

.instruction-text strong {
  color: #2c3e50;
  font-weight: 600;
}

.selected-locations-list {
  margin: 1rem 0;
  padding: 1rem;
  background: white;
  border-radius: 6px;
  border: 1px solid #dee2e6;
  max-height: 200px;
  overflow-y: auto;
}

.selected-location-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0;
  border-bottom: 1px solid #f8f9fa;
}

.selected-location-item:last-child {
  border-bottom: none;
}

.selected-location-item .location-name {
  font-weight: 600;
  color: #2c3e50;
}

.selected-location-item .location-assignment {
  font-size: 0.8rem;
  color: #6c757d;
}

.location-item.multi-selected {
  background: #e3f2fd;
  border-color: #2196f3;
}

.clear-selection-btn {
  margin-top: 0.75rem;
  padding: 0.5rem 1rem;
  background: #6c757d;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.9rem;
  transition: background-color 0.2s;
}

.clear-selection-btn:hover {
  background: #5a6268;
}

.map-container {
  flex: 1;
  position: relative;
  overflow: hidden;
}

.map-display {
  width: 100%;
  height: 100%;
}

.loading-indicator {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background-color: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 1rem 1.5rem;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 500;
  z-index: 10;
  pointer-events: none;
  opacity: 1;
  transition: opacity 0.3s;
}

.loading-indicator:not(.visible) {
  opacity: 0;
}

.loading-state {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 1rem;
  color: #6c757d;
}

.loading-spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid currentColor;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.loading-spinner.large {
  width: 40px;
  height: 40px;
  border-width: 4px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Global marker styles */
:global(.depot-marker) {
  background-color: #e74c3c;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 3px solid #fff;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.4);
  cursor: pointer;
  transition: transform 0.2s;
}

:global(.location-marker) {
  background-color: #2ecc71;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 2px solid #fff;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  cursor: pointer;
  transition: transform 0.2s;
}

:global(.assigned-marker) {
  width: 18px;
  height: 18px;
  border: 3px solid #fff;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.4);
}

:global(.depot-marker:hover),
:global(.location-marker:hover),
:global(.assigned-marker:hover) {
  transform: scale(1.2);
}

:global(.depot-marker.selected),
:global(.location-marker.selected),
:global(.assigned-marker.selected) {
  transform: scale(1.3);
  box-shadow: 0 4px 12px rgba(255, 193, 7, 0.6);
  border-color: #ffc107;
}

:global(.depot-marker.multi-selected),
:global(.location-marker.multi-selected),
:global(.assigned-marker.multi-selected) {
  transform: scale(1.2);
  box-shadow: 0 4px 12px rgba(33, 150, 243, 0.6);
  border-color: #2196f3;
  border-width: 3px;
}

:global(.location-popup) {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif;
  max-width: 300px;
}

:global(.location-popup h4) {
  margin: 0 0 10px 0;
  font-size: 16px;
  font-weight: 600;
  color: #2c3e50;
  border-bottom: 1px solid #ecf0f1;
  padding-bottom: 8px;
}

:global(.location-popup p) {
  margin: 5px 0;
  font-size: 14px;
  line-height: 1.4;
  color: #34495e;
}

:global(.location-popup strong) {
  color: #2c3e50;
  font-weight: 600;
}

/* Responsive design */
@media (max-width: 1200px) {
  .left-panel {
    width: 300px;
  }
  
  .right-panel {
    width: 280px;
  }
}

@media (max-width: 768px) {
  .editor-content {
    flex-direction: column;
  }
  
  .left-panel {
    width: 100%;
    height: 40vh;
    border-right: none;
    border-bottom: 1px solid #dee2e6;
  }
  
  .right-panel {
    width: 100%;
    height: 30vh;
    border-left: none;
    border-top: 1px solid #dee2e6;
    order: 3;
  }
  
  .map-container {
    order: 2;
    height: 30vh;
  }
  
  .editor-header {
    padding: 1rem;
  }
  
  .editor-header h1 {
    font-size: 1.2rem;
  }
  
  .header-left {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }
  
  .instruction-item {
    padding: 0.75rem;
  }
  
  .instruction-icon {
    font-size: 1.2rem;
  }
  
  .instruction-text {
    font-size: 0.8rem;
  }
}
</style>